import {
  User, Calendar, Star, Clock, FileText, CheckCircle2
} from 'lucide-react';
import type { Review } from '../../types';
import {
  getReviewStatusColor, getReviewPeriodLabel 
} from '../../types';

interface ReviewCardProps {
  readonly review: Review;
  readonly onClick?: () => void;
}

const STAR_POSITIONS = ['first', 'second', 'third', 'fourth', 'fifth'] as const;

export function ReviewCard({
  review, onClick 
}: ReviewCardProps) {
  const statusColor = getReviewStatusColor(review.status);
  const periodLabel = getReviewPeriodLabel(review.period);
  
  const dueDate = new Date(review.dueDate);
  const isOverdue = review.status !== 'completed' && dueDate < new Date();
  
  const formattedDueDate = dueDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const periodRange = `${new Date(review.periodStart).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric' 
  })} - ${new Date(review.periodEnd).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric' 
  })}`;

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full rounded-lg border bg-white p-4 text-left transition-shadow hover:shadow-md"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-full bg-primary/10 p-2">
            <User className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">{review.employeeName}</h3>
            <p className="text-sm text-gray-500">{review.employeeRole}</p>
          </div>
        </div>
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${statusColor}`}>
          {review.status.replace('-', ' ')}
        </span>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
          {periodLabel}
        </span>
        <span className="text-xs text-gray-500">{periodRange}</span>
      </div>

      {review.overallRating !== undefined && (
        <div className="mt-3 flex items-center gap-1">
          {STAR_POSITIONS.map((position, starIndex) => (
            <Star
              key={`star-${review.reviewId}-${position}`}
              className={`h-4 w-4 ${
                starIndex < (review.overallRating ?? 0) ? 'fill-amber-400 text-amber-400' : 'text-gray-300'
              }`}
            />
          ))}
          <span className="ml-2 text-sm font-medium text-gray-700">
            {review.overallRating}/5
          </span>
        </div>
      )}

      {review.strengths && (
        <div className="mt-3">
          <p className="line-clamp-2 text-sm text-gray-600">{review.strengths}</p>
        </div>
      )}

      <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1">
            <FileText className="h-3 w-3" />
            Reviewer: {review.reviewerName}
          </span>
        </div>
        <span className={`flex items-center gap-1 ${isOverdue ? 'text-red-600' : ''}`}>
          {review.completedAt ? (
            <>
              <CheckCircle2 className="h-3 w-3 text-green-600" />
              Completed
            </>
          ) : (
            <>
              <Clock className="h-3 w-3" />
              Due: {formattedDueDate}
            </>
          )}
        </span>
      </div>

      <div className="mt-2 text-xs text-gray-400">
        <Calendar className="mr-1 inline h-3 w-3" />
        {review.siteName}
      </div>
    </button>
  );
}
